import { Button, Form, Input } from "@light-technology/components";
import { Controller, useForm } from "react-hook-form";
import { Navigate, useSearchParams } from "react-router";
import { api } from "../../state/api";
import { useDocumentTitle } from "../../utilities/useDocumentTitle";
import { useMutationWithToast } from "../../utilities/useMutationWithToast";
import { PasswordStrength } from "../shared/PasswordStrength";

type ResetPasswordForm = {
  password: string;
  reenter_password: string;
};

export const ResetPasswordPage = () => {
  useDocumentTitle("Reset password");

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [resetPassword, resetPasswordResult] = useMutationWithToast(
    api.useResetPasswordMutation(),
    {},
  );

  const { handleSubmit, control } = useForm<ResetPasswordForm>({
    defaultValues: {
      password: "",
      reenter_password: "",
    },
  });
  const onSubmit = handleSubmit(({ password }) => {
    resetPassword({ password, token });
  });

  if (!token) {
    return <Navigate to="/" />;
  }

  return (
    <Form
      className="flex size-full flex-col justify-center"
      onSubmit={onSubmit}
    >
      <div className="text-2xl font-semibold">Provide a new password</div>
      <Controller
        control={control}
        name="password"
        rules={{ required: "Password is required" }}
        render={({ field, fieldState }) => (
          <>
            <Input
              {...field}
              className="mt-2xl"
              type="password"
              label="Password"
              placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
            <PasswordStrength password={field.value} />
          </>
        )}
      />
      <Controller
        control={control}
        name="reenter_password"
        rules={{
          required: "Please reenter your password",
          validate: (reenter_password, { password }) => {
            if (reenter_password !== password) {
              return "Passwords do not match";
            }
          },
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            className="mt-2xl"
            type="password"
            label="Reenter password"
            placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
            isRequired
            validationBehavior="aria"
            isInvalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )}
      />
      <Button
        className="mt-3xl mb-lg w-full"
        type="submit"
        size="lg"
        isLoading={resetPasswordResult.isLoading}
      >
        Submit
      </Button>
      <Button
        className="w-full"
        color="tertiary"
        size="lg"
        href={`/login?${searchParams}`}
      >
        Return to login
      </Button>
    </Form>
  );
};
